/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAuth } from '../utils/AuthContext';
import { listBookingsForRange, deleteBooking } from '../utils/db/bookings';
import { Trash2 } from 'lucide-react';
import CustomerAutocomplete from '../components/CustomerAutocomplete';

const ViewBookings = () => {
  const { user } = useAuth();
  const { register, handleSubmit, setValue, watch } = useForm();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const fromDateValue = watch('fromDate');
  const toDateValue = watch('toDate');

  const onSubmit = async (data) => {
    if (!user) return;
    // Require customer and both dates before fetching
    if (!selectedCustomer?.id || !data.fromDate || !data.toDate) {
      toast.error('Please enter Customer ID, From Date and To Date.');
      setRows([]);
      setHasSearched(false);
      return;
    }
    setIsLoading(true);
    try {
      const list = await listBookingsForRange(
        user.id,
        selectedCustomer?.id || null,
        data.fromDate || null,
        data.toDate || null
      );
      setRows(list);
      setHasSearched(true);
    } catch (e) {
      toast.error('Failed to fetch bookings');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">View Bookings</h1>
        <p className="text-gray-600">Fetch bookings by entering Customer ID and date range.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <CustomerAutocomplete
            label="Customer ID *"
            placeholder="Type to search by Customer ID"
            onChange={(c) => {
              setSelectedCustomer(c);
              setValue('customerId', c.id);
            }}
            value={selectedCustomer?.id}
            valueLabel={selectedCustomer ? `${selectedCustomer.customer_id} - ${selectedCustomer.customer_name}` : ''}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">From Date *</label>
          <input
            type="date"
            {...register('fromDate')}
            onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">To Date *</label>
          <input
            type="date"
            {...register('toDate')}
            onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div className="flex items-end">
          <button type="submit" disabled={isLoading || !selectedCustomer?.id || !fromDateValue || !toDateValue} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {isLoading ? 'Loading...' : 'Fetch'}
          </button>
        </div>
      </form>

      {hasSearched && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AWB</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receiver</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.awb_no}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.customer_name || r.customer_id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.receiver_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{parseFloat(r.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td className="px-6 py-4 text-center text-gray-500" colSpan="5">No bookings</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ViewBookings;


